import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import apiRequest, { ApiResponse } from "../axios/useApi";
import { UIContext } from "./UIProvider";
import { downloadReport, formatDate, handleApiError } from "../lib/utils";
import moment from "moment";

interface ReportsContextType {
  data: {
    profitAndLoss: {
      profitData: TypeAccordionItemData[];
      taxes: Taxes;
    };
    balanceSheet: TypeAccordionItemData[];
  };
  isLoading: boolean;
  error?: string;
  activeTab: string;
  isExpanded: (tabId: string, itemId: string) => boolean;
  toggleAccordion: (tabId: string, itemId: string) => void;
  date: Date;
  setDate: (date: Date) => void;
  onDateChange: (date: Date) => void;
  handleTabChange: (value: string) => void;
  expandAllRows: (tabId: string) => void;
  collapseAllRows: (tabId: string) => void;
  areAllRowsExpanded: (tabId: string) => boolean;
  enabledReports: { label: string; type: string }[];
  handleDownload: () => void;
  isDownloading: boolean;
}

export interface TypeAccordionItemData {
  id: string;
  name: string;
  type?: string;
  subType?: string;
  balance: number;
  children?: TypeAccordionItemData[];
}

export type ReportType = "profitAndLoss" | "balanceSheet";

export type ReportsData = {
  profitAndLoss: {
    profitData: TypeAccordionItemData[];
    taxes: Taxes;
  };
  balanceSheet: TypeAccordionItemData[];
};

type Props = PropsWithChildren<{}>;

interface ProfitLossItem {
  name: string;
  accountSubTypeName: string;
  accountName: string;
  amount: number;
}

interface BalanceSheetItem {
  name: string;
  accountSubTypeName: string;
  accountName: string;
  amount: number;
}
export interface Taxes {
  amount: number;
  displayName: string;
  name: string;
}
interface ReportDataItem {
  profitLossItems: ProfitLossItem[];
  taxes: Taxes;
}

const formatData = (data: ProfitLossItem[]): TypeAccordionItemData[] => {
  // Grouping data by `name` (REVENUE, EXPENSE)
  const grouped = data.reduce(
    (acc: Record<string, ProfitLossItem[]>, item: ProfitLossItem) => {
      if (!acc[item.name]) {
        acc[item.name] = [];
      }
      acc[item.name].push(item);
      return acc;
    },
    {}
  );

  // Now format the data
  const result = Object.keys(grouped).map((parentKey) => {
    const parentItems = grouped[parentKey];

    // Sum of all amounts for this parent (REVENUE or EXPENSE)
    const parentTotal = parentItems.reduce(
      (sum: number, item: ProfitLossItem) => sum + item.amount,
      0
    );

    // Grouping by `accountSubTypeName` (REVENUE, SALES, etc.)
    const groupedBySubType = parentItems.reduce(
      (subAcc: Record<string, ProfitLossItem[]>, item: ProfitLossItem) => {
        if (!subAcc[item.accountSubTypeName]) {
          subAcc[item.accountSubTypeName] = [];
        }
        subAcc[item.accountSubTypeName].push(item);
        return subAcc;
      },
      {}
    );

    // Now prepare children (grouped by accountSubTypeName)
    const children = Object.keys(groupedBySubType).map((subTypeKey) => {
      const childItems = groupedBySubType[subTypeKey];

      // Calculate the total for each child group
      const childTotal = childItems.reduce(
        (sum: number, item: ProfitLossItem) => sum + item.amount,
        0
      );

      // Format child items to match TypeAccordionItemData
      const formattedChildren = childItems.map((item: ProfitLossItem) => ({
        id: `${subTypeKey.toLowerCase()}-${item.accountName.toLowerCase()}`,
        name: item.accountName,
        balance: item.amount,
        type: subTypeKey,
        subType: item.accountSubTypeName,
      }));

      return {
        id: `${parentKey.toLowerCase()}-${subTypeKey.toLowerCase()}`,
        name: subTypeKey,
        balance: childTotal,
        type: parentKey,
        subType: subTypeKey,
        children: formattedChildren,
      };
    });

    return {
      id: parentKey.toLowerCase(),
      name: parentKey,
      balance: parentTotal,
      type: parentKey,
      children,
    };
  });

  return result;
};

export const ReportsContext = createContext<ReportsContextType>(
  {} as ReportsContextType
);

export const ReportsProvider = ({ children }: Props) => {
  const enabledReports = [
    { label: "Profit and Loss", type: "profitAndLoss" },
    { type: "balanceSheet", label: "Balance Sheet" },
  ];
  const [data, setData] = useState<ReportsData>({
    profitAndLoss: {
      profitData: [],
      taxes: {
        amount: 0,
        displayName: "",
        name: "",
      },
    },
    balanceSheet: [],
  });
  const { onError, onSuccess, businessId } = useContext(UIContext);
  const [isLoading, setIsLoading] = useState(false);
  const [date, setDate] = useState<Date>(new Date());
  const [error, setError] = useState<string | undefined>();
  const [activeTab, setActiveTab] = useState<ReportType>("profitAndLoss");
  const [isDownloading, setIsDownloading] = useState(false);

  const [expandedItems, setExpandedItems] = useState<Record<string, string[]>>(
    () => {
      const initialState: Record<string, string[]> = {};
      enabledReports.forEach((report: any) => {
        initialState[report.type] = [];
      });
      return initialState;
    }
  );

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const fileName =
        activeTab === "profitAndLoss" ? "Profit_And_Loss" : "Balance_Sheet";
      await downloadReport({
        data:
          activeTab === "profitAndLoss"
            ? data.profitAndLoss
            : data.balanceSheet,
        fileName,
        format: "excel",
        type: activeTab === "profitAndLoss" ? "profit-loss" : "balance-sheet",
      });
      onSuccess?.(
        "DOWNLOAD",
        "downloadReport",
        "Report Downloaded Successfully!"
      );
    } catch (error) {
      // Handle error (show toast, etc.)
      onError?.(new Error("An error occurred while downloading report."));
      setIsDownloading(false);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleTabChange = (value: string) => {
    const matched = enabledReports.find((r: any) => r.type === value);
    setIsLoading(true);
    if (matched) {
      setActiveTab(matched.type as ReportType);
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  };

  // Helper function to get all expandable item IDs
  const getAllItemIds = (items: TypeAccordionItemData[]): string[] => {
    return items.reduce((ids: string[], item) => {
      // Only include items with children (expandable items)
      if (item.children && item.children.length > 0) {
        ids.push(item.id);
        ids.push(...getAllItemIds(item.children));
      }
      return ids;
    }, []);
  };

  function getAccordionArray(tabId: string) {
    if (tabId === "profitAndLoss") {
      return data.profitAndLoss.profitData;
    }
    if (tabId === "balanceSheet") {
      return data.balanceSheet;
    }
    return [];
  }

  const toggleAccordion = (tabId: string, itemId: string) => {
    setExpandedItems((prev) => {
      const tabItems = [...(prev[tabId] || [])];
      let newExpandedItems;

      if (tabItems.includes(itemId)) {
        // Remove item from expanded items
        newExpandedItems = {
          ...prev,
          [tabId]: tabItems.filter((id) => id !== itemId),
        };
      } else {
        // Add item to expanded items
        newExpandedItems = { ...prev, [tabId]: [...tabItems, itemId] };

        // Check if all items are now expanded after adding this item
        const allIds = getAllItemIds(getAccordionArray(tabId));
        const newExpandedIds = [...tabItems, itemId];

        // If this is the last item to be expanded, trigger expand all to ensure all nested items are expanded
        if (allIds.length > 0 && newExpandedIds.length === allIds.length) {
          return { ...prev, [tabId]: allIds };
        }
      }

      return newExpandedItems;
    });
  };

  // Check if an accordion item is expanded
  const isExpanded = (tabId: string, itemId: string) => {
    return expandedItems[tabId]?.includes(itemId) || false;
  };

  const onDateChange = (date: Date | undefined) => {
    if (date) {
      let fromDate: Date;
      let toDate: Date;

      // For month type: use first and last day of month
      fromDate = moment(date).startOf("month").toDate();
      toDate = moment(date).endOf("month").toDate();

      setDate(date);
      // Update the API call with the new date range
      if (businessId && activeTab === "profitAndLoss") {
        getProfitAndLossData(
          businessId,
          fromDate.toISOString(),
          toDate.toISOString()
        );
      }
      if (businessId && activeTab === "balanceSheet") {
        getBalanceSheetData(
          businessId,
          fromDate.toISOString(),
          toDate.toISOString()
        );
      }
    }
  };

  const getProfitAndLossData = async (
    businessMasterId: string,
    fromDate: string,
    toDate: string
  ) => {
    setIsLoading(true);
    try {
      const response: ApiResponse<ReportDataItem> = await apiRequest({
        url: `/Report/${businessMasterId}/ProfitAndLoss?FromDate=${formatDate(
          fromDate
        )}&ToDate=${formatDate(toDate)}`,
        method: "GET",
      });

      if (response && response.success) {
        onSuccess?.(
          "GET",
          "getProfitAndLoss",
          response?.message || "Data retrieved successfully!"
        );
        const formatedData = formatData(response?.data?.profitLossItems || []);
        setData({
          ...data,
          profitAndLoss: {
            profitData: formatedData || [],
            taxes: response.data?.taxes || {
              amount: 0,
              displayName: "",
              name: "",
            },
          },
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching AccountsList."));
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  const getBalanceSheetData = async (
    businessMasterId: string,
    fromDate: string,
    toDate: string
  ) => {
    setIsLoading(true);
    try {
      const response: ApiResponse<BalanceSheetItem[]> = await apiRequest({
        url: `/Report/${businessMasterId}/BalanceSheet?FromDate=${formatDate(
          fromDate
        )}&ToDate=${formatDate(toDate)}`,
        method: "GET",
      });

      if (response && response.success) {
        onSuccess?.(
          "GET",
          "getBalanceSheet",
          response?.message || "Data retrieved successfully!"
        );
        const formatedData = formatData(response?.data || []);
        setData({
          ...data,
          balanceSheet: formatedData,
        });
      } else {
        handleApiError(response, onError);
      }
    } catch (error) {
      onError?.(new Error("An error occurred while fetching Balance Sheet."));
    }
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  useEffect(() => {
    if (businessId && activeTab === "profitAndLoss") {
      const fromDate = moment(date).startOf("month").toDate().toISOString();
      const toDate = moment(date).endOf("month").toDate().toISOString();
      getProfitAndLossData(businessId, fromDate, toDate);
    }

    if (businessId && activeTab === "balanceSheet") {
      const fromDate = moment(date).startOf("month").toDate().toISOString();
      const toDate = moment(date).endOf("month").toDate().toISOString();
      getBalanceSheetData(businessId, fromDate, toDate);
    }
  }, [businessId, activeTab]);

  const expandAllRows = (tabId: string) => {
    const allItemIds = getAllItemIds(getAccordionArray(tabId));
    setExpandedItems((prev) => ({
      ...prev,
      [tabId]: allItemIds,
    }));
  };

  const collapseAllRows = (tabId: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [tabId]: [],
    }));
  };

  const areAllRowsExpanded = (tabId: string) => {
    const allExpandableIds = getAllItemIds(getAccordionArray(tabId));
    const expandedIds = expandedItems[tabId] || [];

    // Only consider items with children for the expandable state
    return (
      allExpandableIds.length > 0 &&
      allExpandableIds.every((id) => expandedIds.includes(id))
    );
  };
  return (
    <ReportsContext.Provider
      value={{
        data,
        isLoading,
        error,
        activeTab,
        isExpanded,
        toggleAccordion,
        date,
        setDate,
        onDateChange,
        handleTabChange,
        expandAllRows,
        collapseAllRows,
        areAllRowsExpanded,
        enabledReports,
        handleDownload,
        isDownloading,
      }}
    >
      {children}
    </ReportsContext.Provider>
  );
};
